import { ArrowLeft, ExternalLink, Mail, ShieldCheck, Sparkles } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link } from 'wouter';
import {
  CONTACT_EMAIL,
  EFFECTIVE_DATE,
  LEGAL_LINKS,
  LEGAL_PATHS,
  OPERATOR_NAME,
  OPERATOR_REGION,
  SITE_ORIGIN,
} from '@/lib/site';

type LegalPageProps = {
  eyebrow: string;
  title: string;
  intro: string;
  path: string;
  description: string;
  children: ReactNode;
};

function LegalPage({ eyebrow, title, intro, path, description, children }: LegalPageProps) {
  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute('content');
    const canonical = document.querySelector('link[rel="canonical"]') ?? document.createElement('link');
    const descriptionMeta = document.querySelector('meta[name="description"]') ?? document.createElement('meta');

    document.title = `${title} | Turbans.ai`;
    descriptionMeta.setAttribute('name', 'description');
    descriptionMeta.setAttribute('content', description);
    document.head.appendChild(descriptionMeta);
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${SITE_ORIGIN}${path}`);
    document.head.appendChild(canonical);

    return () => {
      document.title = previousTitle;
      if (previousDescription) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, [description, path, title]);

  return (
    <div className="page-enter">
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1100px] px-5 py-14 sm:px-8 lg:px-12 lg:py-20">
          <Link href="/" className="inline-flex items-center gap-2 text-xs font-semibold text-primary-foreground/70 transition hover:text-secondary">
            <ArrowLeft size={14} /> Back to Turbans.ai
          </Link>
          <p className="mono-font mt-12 text-[10px] uppercase tracking-[.18em] text-secondary">{eyebrow}</p>
          <h1 className="display-font mt-5 max-w-3xl text-5xl leading-[.92] tracking-[-.05em] sm:text-7xl">{title}</h1>
          <p className="mt-6 max-w-2xl text-base leading-7 text-primary-foreground/75">{intro}</p>
          <p className="mt-8 text-xs text-primary-foreground/55">Effective {EFFECTIVE_DATE}</p>
        </div>
      </section>
      <main className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:px-12 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[.72fr_1.28fr]">
          <aside className="h-fit rounded-2xl border border-border bg-card p-6 lg:sticky lg:top-28">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                <ShieldCheck size={19} />
              </span>
              <div>
                <p className="mono-font text-[10px] uppercase tracking-[.14em] text-primary">Turbans.ai</p>
                <p className="mt-1 text-sm font-semibold">Clear, human-readable policies.</p>
              </div>
            </div>
            <nav className="mt-6 grid gap-2 text-sm" aria-label="Legal pages">
              <LegalNavLink href={LEGAL_PATHS.privacy} label="Privacy Policy" />
              <LegalNavLink href={LEGAL_PATHS.terms} label="Terms of Service" />
              <LegalNavLink href={LEGAL_PATHS.subscriptions} label="Subscription Terms" />
              <LegalNavLink href={LEGAL_PATHS.deletion} label="Account deletion" />
              <LegalNavLink href={LEGAL_PATHS.support} label="Support" />
            </nav>
          </aside>
          <article className="legal-copy max-w-[700px]">{children}</article>
        </div>
      </main>
    </div>
  );
}

function LegalNavLink({ href, label }: { href: string; label: string }) {
  return <Link href={href} className="rounded-lg px-3 py-2 font-semibold text-muted-foreground transition hover:bg-muted hover:text-primary">{label}</Link>;
}

function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-border py-8 first:border-t-0 first:pt-0">
      <h2 className="display-font text-3xl leading-none tracking-[-.03em]">{title}</h2>
      <div className="mt-4 space-y-4 text-sm leading-7 text-muted-foreground">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return <ul className="list-disc space-y-2 pl-5">{items.map((item) => <li key={item}>{item}</li>)}</ul>;
}

function ContactCallout({ label = 'Questions or requests?' }: { label?: string }) {
  return (
    <div className="rounded-2xl bg-primary p-6 text-primary-foreground sm:p-7">
      <div className="flex items-start gap-3">
        <Mail className="mt-1 shrink-0 text-secondary" size={19} />
        <div>
          <p className="font-bold">{label}</p>
          <p className="mt-2 text-sm leading-6 text-primary-foreground/75">
            Email <a className="font-semibold text-secondary underline-offset-4 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Turbans.ai is operated by {OPERATOR_NAME} in {OPERATOR_REGION}; no postal address is provided on this site.
          </p>
        </div>
      </div>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalPage
      eyebrow="Privacy Policy"
      title="Your photo stays yours."
      intro="This policy explains what Turbans.ai handles when you explore turban styles and create an AI try-on."
      path="/privacy"
      description="Read the Turbans.ai Privacy Policy: what information is handled, how try-on images are processed, local storage, subscriptions, and deletion choices."
    >
      <LegalSection title="Who operates Turbans.ai">
        <p>Turbans.ai is operated by {OPERATOR_NAME} in {OPERATOR_REGION}. Privacy questions and requests can be sent to <a className="font-semibold text-primary underline-offset-4 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </LegalSection>
      <LegalSection title="Information we handle">
        <BulletList items={[
          'Photos you choose to upload, and the style, colour, occasion, or fit instructions you provide for a try-on.',
          'The generated try-on image returned to your browser or device.',
          'A signed anonymous session identifier used to enforce the free try-on allowance, plus limited request information used for rate limiting and service protection.',
          'Saved looks and favourite styles stored locally in your browser or mobile device when you choose to save them.',
          'For native subscriptions, an anonymous RevenueCat app user identifier and subscription entitlement or purchase status. Apple App Store or Google Play handles payment details.',
        ]} />
      </LegalSection>
      <LegalSection title="How try-on images are processed">
        <p>When you request a try-on, the selected image and your request are sent to Google Gemini through the service integration used by Turbans.ai. The image is used to produce the requested result and is not kept by Turbans.ai on its server after processing is complete.</p>
        <p>Generated images are returned to you. If you explicitly save a look, the app may keep that saved look on your own browser or device until you remove it. This local copy is separate from server retention and is not accessible to Turbans.ai.</p>
      </LegalSection>
      <LegalSection title="Subscriptions and service providers">
        <p>Native subscription purchases are processed through RevenueCat and the applicable store, Apple App Store or Google Play. RevenueCat receives the anonymous app user identifier and purchase state needed to confirm the <strong className="text-foreground">premium</strong> entitlement. Turbans.ai does not receive your card or store payment details.</p>
        <p>Prices and billing currency are supplied by the relevant store. See the <Link className="font-semibold text-primary underline-offset-4 hover:underline" href="/subscription-terms">Subscription Terms</Link> for cancellation, restoration, and refund guidance.</p>
      </LegalSection>
      <LegalSection title="Your choices">
        <BulletList items={[
          'Remove saved looks from the My Looks screen. You can also clear Turbans.ai site data in your browser settings.',
          'Remove saved looks from the Saved Looks screen in the mobile app.',
          `Email ${CONTACT_EMAIL} for a privacy or deletion request. Please do not send a selfie as an email attachment unless requested.`,
        ]} />
      </LegalSection>
      <LegalSection title="Children">
        <p>Turbans.ai is intended for people aged 13 or older, or the higher minimum age required where you live. We do not knowingly invite children under that age to submit photos.</p>
      </LegalSection>
      <ContactCallout label="Privacy request?" />
    </LegalPage>
  );
}

export function TermsOfService() {
  return (
    <LegalPage
      eyebrow="Terms of Service"
      title="A place to explore."
      intro="These terms set the ground rules for using Turbans.ai respectfully and safely."
      path="/terms"
      description="Read the Turbans.ai Terms of Service for acceptable use, AI try-on limitations, user content, availability, and governing law."
    >
      <LegalSection title="Agreement">
        <p>By using Turbans.ai, you agree to these Terms of Service and the <Link className="font-semibold text-primary underline-offset-4 hover:underline" href="/privacy">Privacy Policy</Link>. If you do not agree, please do not use the service.</p>
        <p>Turbans.ai is operated by {OPERATOR_NAME} in {OPERATOR_REGION}. These terms are effective from {EFFECTIVE_DATE}.</p>
      </LegalSection>
      <LegalSection title="Who may use the service">
        <p>You must be at least 13 years old, or meet the higher minimum age required by the laws where you live. If you use Turbans.ai for someone else, you must have permission to use their photo and provide the result to them.</p>
      </LegalSection>
      <LegalSection title="Using the try-on">
        <BulletList items={[
          'Upload only photos you have the right and permission to use.',
          'Do not upload unlawful, abusive, or harmful content, or use the service to identify, profile, or target another person without their permission.',
          'AI results are visual suggestions, not a guarantee of fit, cultural authority, religious guidance, or professional advice.',
          'Review every result yourself. A generated image may be inaccurate or may not represent the way a turban will actually sit or be tied.',
          'Do not rely on Turbans.ai for an urgent, safety-critical, or legally important decision.',
        ]} />
      </LegalSection>
      <LegalSection title="Your content">
        <p>You keep your rights in photos and instructions you provide. You give Turbans.ai the limited permission needed to process that content to provide the requested try-on. Turbans.ai does not claim ownership of your uploaded or generated images.</p>
        <p>Server-side image processing is intended to be temporary. Saved looks are stored locally on your own browser or device only when you choose to save them.</p>
      </LegalSection>
      <LegalSection title="Availability and changes">
        <p>We may improve, change, suspend, or discontinue features. We aim to keep the service useful, but do not promise that it will always be available, error-free, or suitable for every photo, device, or style request.</p>
        <p>We may update these terms when the service or legal requirements change. The effective date above will show when the current version took effect.</p>
      </LegalSection>
      <LegalSection title="Subscriptions">
        <p>Native subscriptions are governed by the store through which you purchased them. The price, duration, renewal details, and included features shown in the current store offering apply to that purchase. See the <Link className="font-semibold text-primary underline-offset-4 hover:underline" href="/subscription-terms">Subscription Terms</Link>.</p>
      </LegalSection>
      <LegalSection title="Governing law">
        <p>These terms are governed by the laws of {OPERATOR_REGION}, without preventing any mandatory consumer protections that apply to you where you live.</p>
      </LegalSection>
      <ContactCallout label="Need to contact the operator?" />
    </LegalPage>
  );
}

export function SupportPage() {
  return (
    <LegalPage
      eyebrow="Support"
      title="Let’s sort it out."
      intro="Tell us what happened and we’ll use the details to help you get back to finding your look."
      path="/support"
      description="Get help with Turbans.ai try-ons, saved looks, subscriptions, restores, privacy requests, and account deletion."
    >
      <LegalSection title="Contact support">
        <p>Email <a className="font-semibold text-primary underline-offset-4 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with a short description of the issue. Please do not send passwords, payment details, or selfie attachments unless support specifically asks for one.</p>
        <p>Useful details include the device type, whether you are using the web app or mobile app, the screen you were on, and the time the issue occurred. For subscription issues, include the store (Apple or Google Play) and whether you tried Restore purchases.</p>
      </LegalSection>
      <LegalSection title="Try these first">
        <BulletList items={[
          'For a failed try-on, check your connection, use a clear JPEG, PNG, or WEBP image, and try again later.',
          'For a missing subscription, open the Plans screen, refresh the offering, then choose Restore purchases.',
          'For saved looks, check the My Looks or Saved Looks screen on the same browser or device where you saved them.',
          'For privacy or deletion requests, email the same support address and say “Privacy request” or “Delete my data” in the subject.',
        ]} />
      </LegalSection>
      <LegalSection title="Subscription help">
        <p>Billing, renewal, cancellation, and refunds are managed by Apple App Store or Google Play. Turbans.ai cannot view your store payment details. You can use <Link className="font-semibold text-primary underline-offset-4 hover:underline" href="/subscription-terms">Subscription Terms</Link> for the relevant store-management links.</p>
      </LegalSection>
      <LegalSection title="Privacy and deletion">
        <p>Read the <Link className="font-semibold text-primary underline-offset-4 hover:underline" href="/privacy">Privacy Policy</Link> for data handling and the <Link className="font-semibold text-primary underline-offset-4 hover:underline" href="/delete-account">deletion instructions</Link> for removing local saved looks or requesting help.</p>
      </LegalSection>
      <ContactCallout label="Support email" />
    </LegalPage>
  );
}

export function AccountDeletionPage() {
  return (
    <LegalPage
      eyebrow="Account deletion"
      title="Remove your Turbans.ai data."
      intro="There is no user sign-up or login account in the current service. Here is how to remove the data the app can keep locally, and how to request help with anything else."
      path="/delete-account"
      description="Learn how to remove Turbans.ai saved looks and request deletion of any service data associated with your anonymous session or mobile app identifier."
    >
      <LegalSection title="No registered account is created">
        <p>The current Turbans.ai service does not offer user registration or login. The native app creates an anonymous app identifier only so a store subscription can be associated with the device and checked for premium access.</p>
      </LegalSection>
      <LegalSection title="Delete saved looks on the web">
        <BulletList items={[
          'Open My Looks.',
          'Use the delete control on each saved look you want to remove.',
          'To clear remaining Turbans.ai local data, clear site data for Turbans.ai in your browser settings. This also removes local favourites and local try-on counters.',
        ]} />
      </LegalSection>
      <LegalSection title="Delete saved looks in the mobile app">
        <p>Open Saved Looks and remove each saved look from the device. If you want to remove all app-local data, delete Turbans.ai from the device using the device’s normal app-storage or uninstall controls.</p>
      </LegalSection>
      <LegalSection title="Request deletion help">
        <p>Email <a className="font-semibold text-primary underline-offset-4 hover:underline" href={`mailto:${CONTACT_EMAIL}?subject=Delete%20my%20Turbans.ai%20data`}>{CONTACT_EMAIL}</a> with “Delete my Turbans.ai data” in the subject. Do not include a selfie or payment information. We may need enough information to identify the relevant anonymous session or device record, but we will not ask for your store password or payment credentials.</p>
      </LegalSection>
      <ContactCallout label="Need deletion help?" />
    </LegalPage>
  );
}

export function SubscriptionTerms() {
  return (
    <LegalPage
      eyebrow="Subscription terms"
      title="Know what you’re choosing."
      intro="These disclosures explain how Turbans.ai native subscriptions are presented, purchased, restored, and managed."
      path="/subscription-terms"
      description="Read Turbans.ai subscription terms for Pro Monthly and Pro Annual, local store pricing, renewals, cancellation, restores, and refunds."
    >
      <LegalSection title="Available plans">
        <p>At launch, Turbans.ai offers <strong className="text-foreground">Pro Monthly</strong> and <strong className="text-foreground">Pro Annual</strong>. Both unlock premium try-ons and saved looks. The exact price and billing currency are shown by Apple App Store or Google Play for your device and region before purchase.</p>
        <p>Neither launch plan includes a free trial.</p>
      </LegalSection>
      <LegalSection title="Store billing and local pricing">
        <p>Native purchases are completed through Apple App Store or Google Play using the store account on your device. The store supplies the customer’s local currency and price. Turbans.ai does not ask for or store your card number.</p>
        <p>RevenueCat securely communicates the purchase state to Turbans.ai so the app can check the <strong className="text-foreground">premium</strong> entitlement. A purchase is not treated as active until that entitlement is confirmed.</p>
      </LegalSection>
      <LegalSection title="Renewal and cancellation">
        <p>Pro Monthly renews every month and Pro Annual renews every year at the price shown by the store until cancelled. Manage or cancel through the store account before the next renewal date. After cancellation, premium access continues until the end of the period already paid for.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <a className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 font-semibold text-foreground transition hover:border-primary hover:text-primary" href="https://apps.apple.com/account/subscriptions" rel="noopener noreferrer">
            Apple subscriptions <ExternalLink size={15} />
          </a>
          <a className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 font-semibold text-foreground transition hover:border-primary hover:text-primary" href="https://play.google.com/store/account/subscriptions" rel="noopener noreferrer">
            Google Play subscriptions <ExternalLink size={15} />
          </a>
        </div>
      </LegalSection>
      <LegalSection title="Restore purchases and refunds">
        <p>Use Restore purchases in the Turbans.ai Plans screen when you reinstall the app or move to another device using the same store account. If the store does not return an active entitlement, access may remain pending until the store and RevenueCat finish syncing.</p>
        <p>Refunds, payment disputes, and store billing questions must be raised with Apple App Store or Google Play, according to the store’s policies. For an entitlement that is paid but not appearing in the app, contact <a className="font-semibold text-primary underline-offset-4 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </LegalSection>
      <LegalSection title="Subscription privacy">
        <p>RevenueCat receives the anonymous app user identifier, product and entitlement status, and information needed to validate the purchase. Apple and Google process the payment. See the <Link className="font-semibold text-primary underline-offset-4 hover:underline" href="/privacy">Privacy Policy</Link> for the broader data disclosure.</p>
      </LegalSection>
      <ContactCallout label="Subscription issue?" />
    </LegalPage>
  );
}

export const legalPageLinks = [
  { href: LEGAL_PATHS.privacy, label: 'Privacy Policy', absolute: LEGAL_LINKS.privacy },
  { href: LEGAL_PATHS.terms, label: 'Terms of Service', absolute: LEGAL_LINKS.terms },
  { href: LEGAL_PATHS.support, label: 'Support', absolute: LEGAL_LINKS.support },
  { href: LEGAL_PATHS.deletion, label: 'Account deletion', absolute: LEGAL_LINKS.deletion },
  { href: LEGAL_PATHS.subscriptions, label: 'Subscription terms', absolute: LEGAL_LINKS.subscriptions },
];

export function LegalBadge() {
  return <span className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Sparkles size={13} className="text-primary" /> Built for thoughtful exploration</span>;
}